import {
  Alert,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { nonUpdatableFormFields } from '../config';
import AdditionalInformation from './AdditionalInformation';
import ModelInformation from './ModelInformation';
import OrderInformation from './OrderInformation';
import { FormikState } from './schema';
import { useExceptionForm } from './useExceptionForm';

export type ExceptionFormProps = {
  form: FormikState;
  isEditForm: boolean;
  fetchError?: string;
  assignedToGroups: OptionDefinition[];
};

export default function ExceptionForm(props: ExceptionFormProps): ReactElement {
  const { exceptionTypes, loadingResolverConfig, fetchConfigError } = useExceptionForm();
  const { form, isEditForm, fetchError, assignedToGroups } = props;
  const { t } = useTranslation();

  if (fetchConfigError) {
    return <Alert type="error">{fetchConfigError}</Alert>;
  }
  // Do not load page until exception type and resovler group info is done being loaded.
  if (loadingResolverConfig) {
    return <Spinner size="big" />;
  }
  return (
    <>
      {!fetchError && (
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header
                variant="h2"
                description=""
                actions={
                  <span>
                    <i className="red-text">*</i> {t('Required field')}
                  </span>
                }>
                {t('Exception Information')}
              </Header>
            }>
            <SpaceBetween direction="vertical" size="m">
              <FormField
                description=""
                label={
                  <span>
                    {t('Title')}
                    <i className="red-text">*</i>
                  </span>
                }
                errorText={form.touched.title && form.errors.title}>
                <Input
                  data-test-id="input-title"
                  value={form.values.title}
                  onChange={(event) => {
                    form.setFieldValue('title', event.detail.value);
                  }}
                  placeholder={t('Enter a title')}
                />
              </FormField>
              <FormField
                description=""
                label={
                  <span>
                    {t('Exception type')}
                    <i className="red-text">*</i>
                  </span>
                }
                errorText={form.touched.exceptionType && form.errors.exceptionType}>
                <Select
                  placeholder={t('Select exception type')}
                  disabled={isEditForm && nonUpdatableFormFields.includes('exceptionType')}
                  id="input-exception-type"
                  data-test-id="input-exception-type"
                  selectedOption={form.values.exceptionType}
                  onChange={(event) => {
                    form.setFieldValue('exceptionType', event.detail.selectedOption, true);
                    form.setFieldValue('assignedToId', null, true);
                  }}
                  options={exceptionTypes}
                />
              </FormField>
              <FormField
                description=""
                label={
                  <span>
                    {t('Assigned To')}
                    <i className="red-text">*</i>
                  </span>
                }
                errorText={form.touched.assignedToId && form.errors.assignedToId}>
                <Select
                  placeholder={t('Select group')}
                  disabled={isEditForm && nonUpdatableFormFields.includes('assignedToId')}
                  data-test-id="input-assigned-to-id"
                  selectedOption={form.values.assignedToId}
                  onChange={(event) => {
                    form.setFieldValue('assignedToId', event.detail.selectedOption, true);
                  }}
                  options={assignedToGroups}
                />
              </FormField>
            </SpaceBetween>
          </Container>
          <OrderInformation form={form} isEditForm={isEditForm} />
          <ModelInformation form={form} isEditForm={isEditForm} />
          <AdditionalInformation form={form} isEditForm={isEditForm} />
        </SpaceBetween>
      )}
      {fetchError && <Alert type="error">{fetchError}</Alert>}
    </>
  );
}
